import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaItemOverview.module.css";
import { ColorStyle } from "src/utils/Colors";
import { AgendaItemDragDrop } from "./dragdrop/AgendaItemDragDrop";
import { LOADING_TYPE } from "src/stores/assemblies/AssemblyStore";
import { AgendaItemUnlockModal } from "./modal/AgendaItemUnlockModal";
import { AGENDAITEM_TABS } from "src/stores/assemblies/AgendaItemStore";
import { AgendaItemsListAllDocuments } from "./details/documents/AgendaItemsListAllDocuments";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { AgendaItemSplitView } from "./splitView/AgendaItemSplitView";

const AgendaItemOverviewBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, uiStore } = useContext(RootStoreContext);

    const [selectedAgendaTab, setSelectedAgendaTab] = useState<string>(AGENDAITEM_TABS.AGENDAITEMLIST);

    // This ref will wrap both the button and the dropdown menu
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Update navigation state using the store
        agendaitemStore.updateNavigation(assemblyStore.currentAssemblyTitle);

        // Load agenda items when component mounts
        agendaitemStore.loadAgendaItems();
    }, [agendaitemStore, assemblyStore.currentAssemblyTitle]);

    // Whenever isDropdownOpen changes, (re)attach or remove the click‐outside listener
    useEffect(() => {
        if (!agendaitemStore.isDropdownOpen) {
            return;
        }

        function handleClickOutside(event: MouseEvent) {
            // If wrapperRef.current exists and click target is NOT inside it, close dropdown
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                agendaitemStore.toggleDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [agendaitemStore, agendaitemStore.isDropdownOpen]);

    const handleDownloadClick = useCallback(async () => {
        agendaitemStore.toggleDropdown();
    }, [agendaitemStore]);

    const handleDropdownOptionClick = useCallback(
        (dropdownOption: number) => async (e: any) => {
            agendaitemStore.toggleDropdown(false);
            await assemblyStore.generateAssemblyRealestatePDF(
                agendaitemStore.currentAssemblyId || "",
                dropdownOption === 2
            );
            agendaitemStore.downloadAgendaItemPDF(dropdownOption);
        },
        [assemblyStore, agendaitemStore]
    );

    const handleSaveClick = useCallback(() => {
        agendaitemStore.saveAgendaItems();
    }, [agendaitemStore]);

    const saveFormButton =
        assemblyStore.loadingType !== LOADING_TYPE.NONE ? (
            <UI.RotatingSpinner noLogo={true} size={30} />
        ) : uiStore.isMobile ? (
            <UI.Icon
                onClick={agendaitemStore.agendaItemsHaveChanged ? handleSaveClick : undefined}
                icon={UI.SVGIcon.Save}
                color={ColorStyle("primary")}
                size={"large"}
                className={!agendaitemStore.agendaItemsHaveChanged ? styles.DisabledIcon : ""}
            />
        ) : (
            <UI.Button
                className={styles.SaveButton}
                onClick={handleSaveClick}
                label={t("labels.save").toString()}
                disabled={!agendaitemStore.agendaItemsHaveChanged}
            />
        );

    const onClickUnlockAgendaItems = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    const onTabsChange = useCallback((selectedTabId: string) => {
        setSelectedAgendaTab(selectedTabId);
    }, []);

    const tabContents = [
        { label: t("screens.assemblies.agenda.agendalist"), id: AGENDAITEM_TABS.AGENDAITEMLIST },
        { label: t("screens.assemblies.agenda.attached_documents"), id: AGENDAITEM_TABS.ALLDOCUMENTS }
    ];

    return (
        <React.Fragment>
            {/* Header Section */}
            <div className={styles.HeaderRow}>
                <UI.Title>{t("screens.realestate.assembly.agenda")}</UI.Title>

                <div className={styles.DownloadButtonContainer}>
                    {/* Wrap both button and dropdown in one div with ref */}
                    <div ref={wrapperRef} className={styles.HeaderButtons}>
                        {assemblyStore.isNewAssemblyPDFExists ? (
                            <button
                                className={uiStore.isMobile ? styles.DownloadButtonMobile : styles.DownloadButton}
                                onClick={handleDownloadClick}
                                title={t("labels.downloads").toString()}
                            >
                                <span className={styles.ButtonContent}>
                                    {uiStore.isMobile ? undefined : <div>{t("labels.downloads")}</div>}
                                    <UI.Icon
                                        icon={
                                            agendaitemStore.isDropdownOpen
                                                ? UI.SVGIcon.ChevronUp
                                                : UI.SVGIcon.ChevronDown
                                        }
                                        color={ColorStyle("primary")}
                                        size={uiStore.isMobile ? 30 : 17}
                                    />
                                </span>
                            </button>
                        ) : (
                            <UI.RotatingSpinner noLogo={true} size={30} />
                        )}

                        {saveFormButton}

                        {/* Dropdown menu */}
                        {agendaitemStore.isDropdownOpen && (
                            <ul className={styles.DropdownDownload}>
                                <li onClick={handleDropdownOptionClick(1)}>
                                    {t("screens.realestate.assembly.download_agendaPdf")}
                                </li>
                                <li onClick={handleDropdownOptionClick(2)}>
                                    {t("screens.realestate.assembly.download_agendaPdf_comments")}
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.Tabbar}>
                <UI.Tabs selectedId={selectedAgendaTab} onTabChange={onTabsChange} contents={tabContents} nowrap />
            </div>

            <div className={styles.AgendaItemContainer}>
                {agendaitemStore.isAgendaItemLocked ? (
                    <>
                        <div className={styles.BlockedBanner}>
                            <div className={styles.BannerLeft}>
                                <UI.Icon className={styles.LockIcon} icon={UI.SVGIcon.Lock} size={"small"} />
                                <div className={styles.BannerText}>{t("screens.assemblies.agenda.locked_info")}</div>
                            </div>
                            <div className={styles.BannerLink} onClick={onClickUnlockAgendaItems}>
                                {t("screens.assemblies.agenda.unlock").toUpperCase()}
                            </div>
                        </div>
                        <AgendaItemUnlockModal />
                    </>
                ) : null}

                {agendaitemStore.currentAgendaItems.length < 0 ? (
                    <UI.RotatingSpinner noLogo={true} size={30} />
                ) : (
                    <>
                        {selectedAgendaTab === AGENDAITEM_TABS.AGENDAITEMLIST ? (
                            <>
                                {uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L ? (
                                    <AgendaItemSplitView />
                                ) : (
                                    <AgendaItemDragDrop />
                                )}
                            </>
                        ) : selectedAgendaTab === AGENDAITEM_TABS.ALLDOCUMENTS ? (
                            <AgendaItemsListAllDocuments />
                        ) : null}
                    </>
                )}
            </div>
        </React.Fragment>
    );
});

export const AgendaItemOverview = Sentry.withProfiler(AgendaItemOverviewBase);
